/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/App.tsx
 * PURPOSE: Main application router với Vietnamese F&B branding và authentic design tokens
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2025-11-08 (edited 2026-09-02 — Issue #450 backend un-shadow, /workflows comment update)
 * COMPLIANCE: SDLC 6.5.0 Enterprise Excellence + Frontend Best Practices + Type Safety
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: Frontend Application, Vietnamese F&B Platform
 * - Impact: Application Quality, User Experience, Performance
 * - Stakeholders: Development Team, End Users, Product Team
 *
 * @technical-architecture
 * - Layer: Frontend Application Layer
 * - Pattern: React Application, TypeScript, Modern Frontend
 * - Dependencies: React 18, TypeScript, Vite, TailwindCSS
 */

import React, { useState, useEffect, Component, ErrorInfo, ReactNode } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { RequireRole } from "./components/auth/RequireRole";
import { RequireAuth } from "./components/auth/RequireAuth";
import { Login } from "./pages/Login"; // CTO FIX Nov 26: Use username/password login
import SsoComplete from "./pages/SsoComplete"; // DD-0090 — nhận vé SSO dùng-một-lần
import { Landing } from "./pages/landing/Landing"; // Sprint 47: Landing Page at root
import { getDashboardRouteForRole } from "./utils/roleDashboardMap"; // SC-001-LOGIN §C: single role->route source
// GỠ import lặp "./styles/design-system.css" (2026-08-03, @coder): đã nạp tại
// main.tsx:31 — cùng một bundle, nạp hai lần không thêm gì ngoài nhiễu thứ tự.

// Platform Admin Imports
import { PlatformAdminProvider } from "./contexts/PlatformAdminContext";
import PlatformAdminLayout from "./components/layout/PlatformAdminLayout"; // Sprint 75 S75-011: SPEC-0416 sidebar
import PlatformAdminDashboard from "./pages/platform-admin/PlatformAdminDashboard";
import PlatformAdminLogin from "./pages/platform-admin/PlatformAdminLogin";
import TenantManagement from "./pages/platform-admin/TenantManagement";
import AdminManagement from "./pages/platform-admin/AdminManagement";
import AuditLogViewer from "./pages/platform-admin/AuditLogViewer";
import IPWhitelistManagement from "./pages/platform-admin/IPWhitelistManagement";

// Sprint 53: Three-Tier Phase 3 Imports
import TenantAdminDashboard from "./pages/tenant-admin/TenantAdminDashboard";
import TenantUserManagement from "./pages/tenant-admin/TenantUserManagement";
// Sprint 73A-1: Tenant Admin sub-pages (TA-002 fix)
import TenantSettings from "./pages/tenant-admin/TenantSettings";
import TenantBotConfig from "./pages/tenant-admin/TenantBotConfig";
import { featureFlags } from "./config/featureFlags";
import TenantAnalytics from "./pages/tenant-admin/TenantAnalytics";
import TenantSecurityDashboard from "./pages/tenant-admin/TenantSecurityDashboard";
// Role Foundation: TenantAdminLayout — auth guard + sidebar (was orphaned, now wired)
import TenantAdminLayout from "./layouts/TenantAdminLayout";
// Release v0 WP-2: ManagerLayout — shell Quản lý (SC-010-HUB-BM app-shell)
import ManagerLayout from "./layouts/ManagerLayout";
// Release v0 WP-2 commit 2: SC-010-HUB-BM — màn Tổng quan Quản lý (thật, nối API)
import ManagerHubPage from "./pages/manager/ManagerHubPage";
import ManagerAttendancePage from "./pages/manager/ManagerAttendancePage";
import ManagerSchedulePage from "./pages/manager/ManagerSchedulePage";
import ManagerTasksPage from "./pages/manager/ManagerTasksPage";
// Release v0 WP-4: SC-012-MGR-STAFF — màn Nhân viên (thật, nối API manager_staff.py)
import ManagerStaffPage from "./pages/manager/ManagerStaffPage";
// Release v0 WP-5: SC-013-MGR-PROFILE — màn Thông tin của tôi (thật, nối API attendance/employee/tenant-users)
import ManagerProfilePage from "./pages/manager/ManagerProfilePage";

// Task Management (SPEC-0713: Urgent/Ad-hoc Task Management)
import { TasksProfessional } from "./pages/TasksProfessional";

// Sprint 10 Dashboard Imports
import LaborCostDashboard from "./pages/CFODashboard/LaborCostDashboard";
import BHXHCompliance from "./pages/HRPortal/BHXHCompliance";
import ConsolidatedFinancials from "./pages/BoardPortal/ConsolidatedFinancials";

// Dashboard Component Imports (SDLC 6.3.1 Design System compliance)
import ChairmanDashboard from "./components/dashboards/ChairmanDashboard";
import ManagerDashboard from "./components/dashboards/ManagerDashboard";
import ManagerApprovalQueue from "./pages/manager/ManagerApprovalQueue";
import ShiftSwapReviewPage from "./pages/manager/ShiftSwapReviewPage";
import DelegationManagement from "./pages/manager/DelegationManagement";
import { EmployeeDashboard } from "./pages/dashboards/EmployeeDashboard";

// Sprint 68: Missing page routes (S68-011 Part A)
import { WorkflowDashboard } from "./pages/Workflows";
import { CreateWorkflow } from "./pages/Workflows";
import { ComplianceOverride } from "./pages/Compliance";
import { SOPComplianceDashboard } from "./pages/SOPCompliance";
import AttendanceModern from "./pages/AttendanceModern";
import { ScheduleModern } from "./pages/ScheduleModern";
import { ScheduleProposal } from "./pages/ScheduleProposal";
import { ShiftSwapModern } from "./pages/ShiftSwapModern";
import { LeaveRequestModern } from "./pages/LeaveRequestModern";
import { PublicHolidaysModern } from "./pages/PublicHolidaysModern";
import { ProfilePage } from "./pages/ProfilePage";
import { KPISelfAssessment } from "./pages/KPISelfAssessment";
import { KPIManagerReview } from "./pages/KPIManagerReview";
import { TrainingPortal } from "./pages/TrainingPortal";
import UsersModern from "./pages/UsersModern";
import OrganizationManagement from "./pages/OrganizationManagement";
import EmployeeManagement from "./pages/EmployeeManagement"; // BR-PLAT-001: Employee CRUD frontend screen
// BR-COMP-002 §8: Job Family / Pay Grade master-data CRUD + Excel import/export
import JobFamiliesPage from "./pages/CompensationMasterData/JobFamilies";
import PayGradesPage from "./pages/CompensationMasterData/PayGrades";
import Reports from "./pages/Reports";
import ComplaintsPage from "./pages/ComplaintsPage"; // Sprint 79-80: Complaint list (BLOCKER-6 fix)
import ChangePassword from "./pages/ChangePassword"; // WP-1 D-5: wire previously orphaned page
// web-sync checklist A.3 (2026-08-15): "Tiện ích" launcher page
import Utilities from "./pages/Utilities";
// web-sync checklist A.4 (2026-08-15): unlocks EmployeeDashboard "Lịch sử" tile
import AttendanceHistory from "./pages/AttendanceHistory";

// ========================================
// DASHBOARD COMPONENTS
// ========================================
// ChairmanDashboard → imported from ./components/dashboards/ChairmanDashboard
// ManagerDashboard → imported from ./components/dashboards/ManagerDashboard
// EmployeeDashboard → imported from ./pages/dashboards/EmployeeDashboard
// CEODashboard → inline below (no separate component file yet)

// CEO Dashboard (inline — no separate component file yet)
// Chairman + Manager + Staff dashboards → imported from component files (see imports above)

// CEO Dashboard — fetches KPIs from analytics API (S68-011 Part C)
const CEODashboard: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [kpis, setKpis] = useState({ efficiency: "—", satisfaction: "—", growth: "—" });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const authStorage = localStorage.getItem("auth-storage");
    const authData = authStorage ? JSON.parse(authStorage) : null;
    const userData = authData?.state?.user;
    if (userData) {
      setUser(userData);
    }

    // Fetch KPIs from analytics API
    const token = authData?.state?.token;
    if (token) {
      fetch("/api/v1/analytics/labor-cost/summary", {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data) {
            setKpis({
              efficiency: data.operational_efficiency
                ? `${Math.round(data.operational_efficiency)}%`
                : "—",
              satisfaction: data.customer_satisfaction
                ? `${data.customer_satisfaction}/5`
                : "—",
              growth: data.monthly_growth
                ? `${data.monthly_growth > 0 ? "+" : ""}${Math.round(data.monthly_growth)}%`
                : "—",
            });
          }
        })
        .catch(() => {
          // Analytics API unavailable — KPIs show default values
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold">CEO Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">
                Welcome, {user?.name || "CEO"}
              </span>
              <button
                onClick={handleLogout}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium mb-2">Operational Efficiency</h3>
            <p className={`text-3xl font-bold text-blue-600 ${loading ? "animate-pulse" : ""}`}>
              {kpis.efficiency}
            </p>
            <p className="text-sm text-gray-500">Hiệu quả vận hành</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium mb-2">Customer Satisfaction</h3>
            <p className={`text-3xl font-bold text-green-600 ${loading ? "animate-pulse" : ""}`}>
              {kpis.satisfaction}
            </p>
            <p className="text-sm text-gray-500">Đánh giá khách hàng</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium mb-2">Monthly Growth</h3>
            <p className={`text-3xl font-bold text-purple-600 ${loading ? "animate-pulse" : ""}`}>
              {kpis.growth}
            </p>
            <p className="text-sm text-gray-500">Tăng trưởng doanh thu</p>
          </div>
        </div>

        <div className="mt-8 bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Operations Overview</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 border rounded">
              <div>
                <p className="font-medium">Strategic Planning</p>
                <p className="text-sm text-gray-500">Q1 2026 objectives tracking</p>
              </div>
              <span className="text-green-600">✓</span>
            </div>
            <div className="flex justify-between items-center p-3 border rounded">
              <div>
                <p className="font-medium">Performance Metrics</p>
                <p className="text-sm text-gray-500">KPI dashboard active</p>
              </div>
              <span className="text-green-600">✓</span>
            </div>
            <div className="flex justify-between items-center p-3 border rounded">
              <div>
                <p className="font-medium">Branch Expansion</p>
                <p className="text-sm text-gray-500">Expansion planning in progress</p>
              </div>
              <span className="text-yellow-600">→</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// ManagerDashboard → imported from ./components/dashboards/ManagerDashboard (see imports)
// StaffDashboard → replaced by EmployeeDashboard from ./pages/dashboards/EmployeeDashboard

// ========================================
// ERROR BOUNDARY (BUG-008 P2a)
// ========================================

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class AppErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[AppErrorBoundary] Uncaught error:', error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", sans-serif',
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.95)', borderRadius: '20px',
            padding: '40px', textAlign: 'center', maxWidth: '480px',
            boxShadow: '0 8px 32px rgba(31,38,135,0.25)',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#333', marginBottom: '8px' }}>
              Lỗi ứng dụng
            </h2>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '24px' }}>
              Đã xảy ra lỗi không mong muốn. Vui lòng tải lại trang.
            </p>
            {this.state.error && (
              <pre style={{
                fontSize: '12px', color: '#999', background: '#f5f5f5',
                padding: '12px', borderRadius: '8px', textAlign: 'left',
                overflowX: 'auto', marginBottom: '24px', maxHeight: '120px',
              }}>
                {this.state.error.message}
              </pre>
            )}
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '12px 28px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none', borderRadius: '12px',
                color: 'white', fontSize: '16px', fontWeight: '600', cursor: 'pointer',
              }}
            >
              Tải lại trang
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ========================================
// MAIN APP COMPONENT
// ========================================

/**
 * Main Application Router
 * Routes:
 *   - /login: Tenant user login (SC-001-LOGIN, email + password, route-by-role)
 *   - /dashboard/chairman: Chairman dashboard (role-based)
 *   - /dashboard/ceo: CEO dashboard (role-based)
 *   - /dashboard/manager: Manager dashboard (role-based)
 *   - /dashboard/staff: Staff dashboard (role-based)
 *   - /cfo/labor-cost: CFO Labor Cost Dashboard (Sprint 10 EPIC 10.1)
 *   - /hr/bhxh-compliance: HR BHXH Compliance Portal (Sprint 10 EPIC 10.2)
 *   - /board/consolidated-financials: Board Consolidated Financials (Sprint 10 EPIC 10.3)
 */
// Auth guard: /dashboard → redirect to role dashboard or /login if token invalid/expired
//
// SC-001-LOGIN §C (WIREFRAME-V0-SCREEN-SPEC-MAP.md) — role -> home_route is now a
// single source (utils/roleDashboardMap.ts, mirrored server-side by
// TenantUserRole.get_home_route()). This guard prefers the server-provided
// `homeRoute` persisted at login time; the inline TenantUserRole branch below is
// only a fallback for sessions persisted before that field existed. CHAIRMAN /
// CEO / DIRECTOR / GM are NOT TenantUserRole values (no such role in the backend
// 14-role enum) — kept as-is as a defensive fallback for any other role source
// that predates the tenant-users login path.
const DashboardGuard: React.FC = () => {
  try {
    const raw = localStorage.getItem('auth-storage');
    const data = raw ? JSON.parse(raw) : null;
    const token = data?.state?.accessToken || data?.state?.token;
    if (!token) return <Navigate to="/login" replace />;
    // Check JWT expiry by decoding payload (no verification needed client-side)
    const parts = token.split('.');
    if (parts.length === 3) {
      const payload = JSON.parse(atob(parts[1]));
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        return <Navigate to="/login" replace />;
      }
    }
    const homeRoute: string | null = data?.state?.homeRoute || null;
    if (homeRoute) return <Navigate to={homeRoute} replace />;

    const role = data?.state?.user?.role?.toUpperCase() || '';
    if (role === 'CHAIRMAN') return <Navigate to="/dashboard/chairman" replace />;
    if (role === 'CEO' || role === 'DIRECTOR') return <Navigate to="/dashboard/ceo" replace />;
    if (role === 'MANAGER' || role === 'GM') return <Navigate to="/dashboard/manager" replace />;
    return <Navigate to={getDashboardRouteForRole(role)} replace />;
  } catch {
    return <Navigate to="/login" replace />;
  }
};

const App: React.FC = () => {
  // Release v0 WP-2: đủ 13 TenantUserRole (mirror backend 14-role enum trừ
  // platform-admin) — dùng để đóng các route đang mở với RequireRole.
  const ALL_TENANT_ROLES = [
    "TENANT_OWNER", "TENANT_ADMIN", "CORPORATE_MANAGER", "BRANCH_MANAGER",
    "SHIFT_MANAGER", "HEAD_CHEF", "CHEF", "WAITER", "CASHIER",
    "KITCHEN_STAFF", "DELIVERY_STAFF", "CLEANER", "TRAINEE",
    // ISS-0013 (2026-07-29, @tester qua E2E TEST-003): EMPLOYEE là enum hợp lệ
    // (S95L-022 fallback) — thiếu nó khiến user EMPLOYEE login xong bị guard
    // bật về /login mãi mãi.
    "EMPLOYEE",
  ];
  // BS-024-R01 (SC-024-EMP-LEAVE MA TRẬN PHÂN QUYỀN, 2026-09-24): mọi vai trò
  // staff + quản lý vào được /leave-requests với tư cách Nhân viên (xem đơn
  // của chính mình); TENANT_ADMIN/TENANT_OWNER KHÔNG có lối vào (không phải
  // Platform Admin — hệ identity đó không nằm trong danh sách role tenant này
  // nên không cần lọc riêng).
  const LEAVE_SELF_SERVICE_ROLES = ALL_TENANT_ROLES.filter(
    (role) => role !== "TENANT_OWNER" && role !== "TENANT_ADMIN"
  );
  return (
    <BrowserRouter>
      <PlatformAdminProvider>
      <AppErrorBoundary>
      <Routes>
        {/* Sprint 52: Tenant user login — email-based tenant resolution */}
        <Route path="/login" element={<Login />} />
        {/* DD-0090 — SSO callback chuyển hướng về đây kèm vé dùng-một-lần */}
        <Route path="/auth/sso/complete" element={<SsoComplete />} />

        {/* Auth guard: /dashboard → role dashboard or /login */}
        <Route path="/dashboard" element={<DashboardGuard />} />

        {/* Role-based Dashboards — Sprint 76 S76-009: RequireRole guards */}
        <Route path="/dashboard/chairman" element={
          <RequireRole allowedRoles={["CHAIRMAN", "TENANT_OWNER"]}>
            <ChairmanDashboard />
          </RequireRole>
        } />
        <Route path="/dashboard/ceo" element={
          <RequireRole allowedRoles={["CHAIRMAN", "DIRECTOR", "TENANT_OWNER"]}>
            <CEODashboard />
          </RequireRole>
        } />
        <Route path="/dashboard/manager" element={
          <RequireRole allowedRoles={["BRANCH_MANAGER", "SHIFT_MANAGER", "HEAD_CHEF", "DIRECTOR", "CHAIRMAN", "TENANT_OWNER", "CORPORATE_MANAGER"]}>
            <ManagerDashboard />
          </RequireRole>
        } />
        {/* Sprint 75 S75-005: Manager Approval Queue */}
        <Route path="/dashboard/manager/approvals" element={
          <RequireRole allowedRoles={["BRANCH_MANAGER", "SHIFT_MANAGER", "HEAD_CHEF", "DIRECTOR", "CHAIRMAN", "TENANT_OWNER"]}>
            <ManagerApprovalQueue />
          </RequireRole>
        } />
        {/* Sprint 76 DD-0023: Delegation Management */}
        <Route path="/dashboard/manager/delegations" element={
          <RequireRole allowedRoles={["BRANCH_MANAGER", "SHIFT_MANAGER", "HEAD_CHEF", "DIRECTOR", "CHAIRMAN", "TENANT_OWNER"]}>
            <DelegationManagement />
          </RequireRole>
        } />
        {/* Release v0 WP-2: was mounted with zero guard — closed with RequireRole
            (đủ 13 tenant roles; vai trò lạ/platform-admin → /login, tránh vòng
            redirect về chính /dashboard/staff). */}
        <Route path="/dashboard/staff" element={
          <RequireRole allowedRoles={ALL_TENANT_ROLES} redirectTo="/login">
            <EmployeeDashboard />
          </RequireRole>
        } />

        {/* Sprint 10: Analytics Dashboards — Sprint 76 S76-009: RequireRole guards */}
        <Route path="/cfo/labor-cost" element={
          <RequireRole allowedRoles={["CHAIRMAN", "DIRECTOR", "TENANT_OWNER"]}>
            <LaborCostDashboard />
          </RequireRole>
        } />
        <Route path="/hr/bhxh-compliance" element={
          <RequireRole allowedRoles={["CHAIRMAN", "DIRECTOR", "BRANCH_MANAGER", "TENANT_OWNER"]}>
            <BHXHCompliance />
          </RequireRole>
        } />
        <Route path="/board/consolidated-financials" element={
          <RequireRole allowedRoles={["CHAIRMAN", "TENANT_OWNER"]}>
            <ConsolidatedFinancials />
          </RequireRole>
        } />

        {/* Release v0 WP-2: Manager shell — /manager redirect + 5 route bọc
            ManagerLayout (guard nhóm quản lý bên trong layout). Hub THẬT (WP-2,
            SC-010-HUB-BM) + Chấm công THẬT (WP-3, SC-011-ATTEND-MGR)
            + Nhân viên THẬT (WP-4, SC-012-MGR-STAFF); 2 route còn
            lại sẽ được thay ở WP-5..WP-6. */}
        <Route path="/manager" element={<Navigate to="/manager/hub" replace />} />
        <Route element={<ManagerLayout />}>
          <Route path="/manager/hub" element={<ManagerHubPage />} />
          <Route path="/manager/schedule" element={<ManagerSchedulePage />} />
          <Route path="/manager/attendance" element={<ManagerAttendancePage />} />
          <Route path="/manager/staff" element={<ManagerStaffPage />} />
          <Route path="/manager/tasks" element={<ManagerTasksPage />} />
          <Route path="/manager/profile" element={<ManagerProfilePage />} />
          {/* Nối lại ManagerApprovalQueue (Sprint 75 S75-005) vào shell mới —
              trước đây chỉ mount ở /dashboard/manager/approvals (ngoài
              ManagerLayout), không có NavLink nào trỏ tới nên mồ côi khỏi
              luồng điều hướng Release v0. Component/API giữ nguyên, chỉ thêm
              route + NavLink trỏ vào (xem ManagerLayout.tsx NAV_ITEMS). */}
          <Route path="/manager/leave-approvals" element={<ManagerApprovalQueue />} />
          {/* DD-0106 (2026-08-30): màn "Đổi ca" riêng — tách khỏi
              ManagerApprovalQueue (nghỉ phép) để dễ thêm dấu hiệu cảnh báo
              riêng cho luồng đổi ca (24h, target chưa đồng ý...). */}
          <Route path="/manager/shift-swaps" element={<ShiftSwapReviewPage />} />
          {/* BUG THẬT đã sửa (2026-08-18, phát hiện live — báo cáo người dùng
              "modal Giao việc hôm qua khác hôm nay"): dòng "2026-08-16" ngay
              dưới đây từng đăng ký TasksProfessional embedded TẠI CÙNG
              path "/manager/tasks" với ManagerTasksPage (dòng 467) — 2 Route
              trùng path trong React Router, route đăng ký TRƯỚC
              (ManagerTasksPage) thắng, route này (TasksProfessional) từ đó
              MỒ CÔI hoàn toàn — không sidebar/link nào tới được, dù code vẫn
              còn. Theo quyết định người dùng: giữ ManagerTasksPage (bản giao
              hàng loạt nhiều NV/nhiều ngày + mốc "Theo thời gian" + Duyệt/Từ
              chối, sửa 2026-08-18) tại "/manager/tasks", xoá route trùng
              này. TasksProfessional KHÔNG bị xoá — vẫn phục vụ Employee
              nguyên vẹn ở route "/tasks" riêng (xem cuối file). */}
        </Route>

        {/* Platform Admin Routes — Sprint 75 S75-011: SPEC-0416 sidebar layout */}
        <Route path="/platform-admin/login" element={<PlatformAdminLogin />} />
        <Route element={<PlatformAdminLayout />}>
          <Route path="/platform-admin/dashboard" element={<PlatformAdminDashboard />} />
          <Route path="/platform-admin/tenants" element={<TenantManagement />} />
          <Route path="/platform-admin/admins" element={<AdminManagement />} />
          <Route path="/platform-admin/audit-logs" element={<AuditLogViewer />} />
          <Route path="/platform-admin/ip-whitelist" element={<IPWhitelistManagement />} />
        </Route>

        {/* Sprint 53 (S53-004, S53-005): Tenant Admin Routes — wrapped with TenantAdminLayout
            Role Foundation: Auth guard + sidebar centralized (was duplicated in 6 pages).
            DD-0002 §4: TENANT_ADMIN + TENANT_OWNER access guard. */}
        <Route element={<TenantAdminLayout />}>
          <Route path="/tenant-admin/dashboard" element={<TenantAdminDashboard />} />
          <Route path="/tenant-admin/users" element={<TenantUserManagement />} />
          {/* Sprint 73A-1: Tenant Admin sub-pages (TA-002 fix) */}
          <Route path="/tenant-admin/settings" element={<TenantSettings />} />
          {/* Pilot: Bot Config UI is hidden (CEO 04/10 via @ceo, #1217). Default OFF via
              VITE_FEATURE_CHATBOT; the page stays on disk. Unmatched -> "*" redirect. */}
          {featureFlags.chatbot && (
            <Route path="/tenant-admin/bot-config" element={<TenantBotConfig />} />
          )}
          <Route path="/tenant-admin/analytics" element={<TenantAnalytics />} />
          <Route path="/tenant-admin/security" element={<TenantSecurityDashboard />} />
          {/* DD-0052 UX-Q6: tenant-admin alias route for org tree management.
              Same component as /organizations (App.tsx:572) but mounted under
              tenant-admin namespace for URL consistency with other TA routes
              (/tenant-admin/users, /tenant-admin/settings, etc).
              Architect default — pending @cpo ratification at G2.
              See DD-0052 v0.1.0 §5.1 + AC-UX-1. */}
          <Route path="/tenant-admin/organizations" element={<OrganizationManagement />} />
          {/* SC6-2 (2026-07-30, ISS-0020): route /tenant-admin/organizations/new ẨN khỏi v0 —
              Thêm đơn vị dùng modal [10] in-page theo registry SC-006 L959.
              OrganizationCreate.tsx giữ lại trên disk (không xoá, mở lại khi cần). */}
          {/* BR-PLAT-001: Employee CRUD frontend screen — MasterDataCrudPage (PR #407)
              wired to the existing Employee REST API (backend/app/api/v1/employees.py).
              Mounted under TenantAdminLayout (TENANT_ADMIN/TENANT_OWNER guard) matching
              the Organizations tenant-admin precedent — Employee CRUD is HR/Tenant-Admin
              scoped, not a general-access page like /attendance or /schedule. */}
          <Route path="/tenant-admin/employees" element={<EmployeeManagement />} />
          {/* BR-COMP-002 §8 R1 — Job Family / Pay Grade master-data CRUD + Excel.
              Mounted INSIDE TenantAdminLayout, whose guard is the security-relevant
              boundary (TENANT_ADMIN/TENANT_OWNER only). Per the @cpo ratification
              review (2026-07-09) these two screens are TENANT_ADMIN-tier ONLY —
              Branch Manager gets no access, not even read, because compensation
              multipliers and pay bands are sensitive. The backend enforces the
              same on every route (check_admin_or_above), including GET/export —
              the layout guard is defense in depth, not the only check (cf. the
              RBAC-bypass class of bug fixed in PR #437). */}
          <Route
            path="/tenant-admin/compensation/job-families"
            element={<JobFamiliesPage />}
          />
          <Route path="/tenant-admin/compensation/pay-grades" element={<PayGradesPage />} />
        </Route>

        {/* Sprint 68 (S68-011): Missing page routes */}
        {/* GUARD: /workflows and /workflows/create were mounted with ZERO guard — not even
            authentication — the same class of gap PR #437 fixed elsewhere. Wrapped in
            RequireAuth (authentication; no ROUTE-level role tier — see below for why).

            UPDATE 2026-09-02 (Issue #450 resolved, BR-BPM-004 APPROVED): the backend
            shadowing described below is FIXED — backend/app/api/v1/workflows.py was
            renamed workflow_engine.py to remove the package/module name collision, and
            it is now genuinely mounted alongside the api/v1/workflows/ package
            (backend-route-manifest.json regenerate will show 11 live /api/v1/workflows/*
            routes). RBAC was added AT THE SAME TIME (Issue #450 found the router had NO
            role/ownership check at all — the paragraph below only describes "no ROLE
            dependency", which underclaimed the gap: there was no ownership check either,
            so any authenticated tenant user could act on any OTHER employee's instance,
            not just their own). The fix is per-INSTANCE, not per-route, which is why
            RequireAuth (authentication only) at the route level is still correct — see
            backend/app/api/v1/workflow_engine.py's `_require_manager_or_assignee`:
            manager-tier-or-above may act on any instance in their tenant, the assigned
            employee may act on their own, anyone else gets 404. A manager-only
            RequireRole list would STILL be UI theater contradicting the backend (the
            reasoning below remains correct) — it would just ALSO now diverge from a
            live, enforced backend guard instead of a dead one.

            GN-11 dead links: STILL not fixed here — this PR intentionally scoped to
            backend un-shadow + RBAC + BR ratification, not this frontend routing
            decision. WorkflowDashboard still navigates to /workflows/instances and
            /workflows/instances/:id, and the pages (WorkflowInstances.tsx,
            WorkflowInstanceDetail.tsx) are still unrouted orphans — but they would no
            longer 404 if mounted, since the backend they call is now live. Mounting them
            (or deciding not to) is a real, separate frontend change; tracked as a
            follow-up to Issue #450, not done in that PR. */}
        <Route path="/workflows" element={
          <RequireAuth>
            <WorkflowDashboard />
          </RequireAuth>
        } />
        <Route path="/workflows/create" element={
          <RequireAuth>
            <CreateWorkflow />
          </RequireAuth>
        } />
        {/* SECURITY FIX (GN-12, PR #432): was mounted with zero guard — any
            authenticated session (incl. plain Employee) could reach compliance
            override actions. Component (ComplianceOverride.tsx) internally
            defaults unmapped roles to 'branch_manager' tier and backend
            (compliance_override.py) enforces per-action tier authorization with
            BRANCH_MANAGER as the minimum override tier — so route guard mirrors
            the existing manager-tier dashboard precedent (line 484). */}
        <Route path="/compliance/overrides" element={
          <RequireRole allowedRoles={["BRANCH_MANAGER", "SHIFT_MANAGER", "HEAD_CHEF", "TENANT_ADMIN", "DIRECTOR", "CHAIRMAN", "TENANT_OWNER", "CORPORATE_MANAGER"]}>
            <ComplianceOverride />
          </RequireRole>
        } />
        {/* SECURITY FIX (GN-12, PR #432): was mounted with zero guard. Aggregated
            SOP-compliance analytics dashboard (header: "Stakeholders: Operations
            Team, Management") — backend (sop_analytics.py) only checks
            authentication, not role, so no backend grounding available; guard
            mirrors the closest structurally-analogous existing route,
            /hr/bhxh-compliance (line 508-511), exactly. */}
        <Route path="/sop-compliance" element={
          <RequireRole allowedRoles={["CHAIRMAN", "DIRECTOR", "BRANCH_MANAGER", "TENANT_OWNER"]}>
            <SOPComplianceDashboard />
          </RequireRole>
        } />
        {/* Release v0 WP-2: 3 route nhân viên đang mở (zero guard) → RequireRole
            đủ 13 tenant roles; unauthorized về /dashboard/staff (default). */}
        <Route path="/attendance" element={
          <RequireRole allowedRoles={ALL_TENANT_ROLES}>
            <AttendanceModern />
          </RequireRole>
        } />
        <Route path="/schedule" element={
          <RequireRole allowedRoles={ALL_TENANT_ROLES}>
            <ScheduleModern />
          </RequireRole>
        } />
        {/* 2026-09-04: "Đăng ký ca làm việc" — luồng nhân viên tự đề xuất ca.
            Trước đây CHỈ có ở mobile (mobile/src/screens/schedule/
            ScheduleProposalScreen.tsx); backend đã sẵn 3 endpoint
            /api/v2/schedules/proposals/* từ 2026-08-09. Cùng tầng guard
            ALL_TENANT_ROLES với các route tự phục vụ khác. */}
        <Route path="/schedule/register" element={
          <RequireRole allowedRoles={ALL_TENANT_ROLES}>
            <ScheduleProposal />
          </RequireRole>
        } />
        <Route path="/tasks" element={<TasksProfessional />} />
        {/* 2026-08-19 (BUG THẬT FG2 — route sống, link từ EmployeeDashboard,
            0 guard): thiếu RequireRole duy nhất trong nhóm route nhân viên
            này — mọi route lân cận (attendance/schedule/profile) đều có.
            Đây chính là đường sống phơi bug backend G5
            (ShiftSwap.branch_id) cho bất kỳ ai đăng nhập web, không cần
            quyền đặc biệt. Thêm guard đối xứng với các route khác. */}
        <Route path="/shift-swap" element={
          <RequireRole allowedRoles={ALL_TENANT_ROLES}>
            <ShiftSwapModern />
          </RequireRole>
        } />
        <Route path="/profile" element={
          <RequireRole allowedRoles={ALL_TENANT_ROLES}>
            <ProfilePage />
          </RequireRole>
        } />
        {/* BS-301 Route 6 boundary B6 (2026-08-11): was mounted with zero guard
            (registry SC-024 gap G5) — closed with RequireRole, same ALL_TENANT_ROLES
            tier as the other 3 employee self-service routes above.
            2026-09-24 (BS-024-R01, SC-024-EMP-LEAVE MA TRẬN PHÂN QUYỀN): tightened
            from ALL_TENANT_ROLES to LEAVE_SELF_SERVICE_ROLES — the wireframe
            explicitly excludes TENANT_ADMIN/TENANT_OWNER from this screen
            ("TA/Owner/Platform Admin → không có lối vào"), which ALL_TENANT_ROLES
            (still used by /public-holidays below, whose own BS-032-R01 explicitly
            includes TA/Owner) did not enforce. */}
        <Route path="/leave-requests" element={
          <RequireRole allowedRoles={LEAVE_SELF_SERVICE_ROLES}>
            <LeaveRequestModern />
          </RequireRole>
        } />
        <Route path="/leave-requests/balance" element={
          <RequireRole allowedRoles={LEAVE_SELF_SERVICE_ROLES}>
            <LeaveRequestModern />
          </RequireRole>
        } />
        {/* SC-032-EMP-PUBLIC-HOLIDAYS (2026-09-24, BS-032-R01): mọi vai trò
            tenant xem được, KỂ CẢ TA/Owner (tra cứu — quản lý là màn riêng
            SC-033) — dùng ALL_TENANT_ROLES nguyên bản, khác /leave-requests. */}
        <Route path="/public-holidays" element={
          <RequireRole allowedRoles={ALL_TENANT_ROLES}>
            <PublicHolidaysModern />
          </RequireRole>
        } />
        <Route path="/kpi/self-assessment" element={<KPISelfAssessment />} />
        {/* web-sync checklist A.3 (2026-08-15): "Tiện ích" launcher — mirrors
            mobile's Tiện ích tab; guard matches the other employee
            self-service routes (/attendance, /schedule, /leave-requests). */}
        <Route path="/utilities" element={
          <RequireRole allowedRoles={ALL_TENANT_ROLES}>
            <Utilities />
          </RequireRole>
        } />
        {/* web-sync checklist A.4 (2026-08-15): attendance history — reuses
            useSelfAttendanceHistory (built for Manager profile) for a plain
            employee; guard matches the other employee self-service routes. */}
        <Route path="/attendance/history" element={
          <RequireRole allowedRoles={ALL_TENANT_ROLES}>
            <AttendanceHistory />
          </RequireRole>
        } />
        {/* SECURITY FIX (GN-12, PR #432): was mounted with zero guard. Backend
            (app/api/v2/endpoints/kpi.py "/manager/pending") requires
            check_manager_or_above() = TenantUserRole hierarchy level <= 4, i.e.
            exactly {TENANT_OWNER, TENANT_ADMIN, CORPORATE_MANAGER, BRANCH_MANAGER,
            SHIFT_MANAGER, HEAD_CHEF} (app/models/tenant_user.py:236-241) — guard
            mirrors backend enforcement exactly. */}
        <Route path="/kpi/manager" element={
          <RequireRole allowedRoles={["TENANT_OWNER", "TENANT_ADMIN", "CORPORATE_MANAGER", "BRANCH_MANAGER", "SHIFT_MANAGER", "HEAD_CHEF"]}>
            <KPIManagerReview />
          </RequireRole>
        } />
        <Route path="/training" element={<TrainingPortal />} />
        {/* SECURITY FIX (GN-12, PR #432): was mounted with zero guard. Page
            comment "Get paginated user list (managers/admin only)" + file header
            "Stakeholders: Tenant Admins, Managers, HR Team" — backend
            (app/api/v1/users.py) currently has its role check commented out
            (pre-existing separate backend gap, not fixed here — out of scope for
            this frontend-guard PR), so guard is grounded in doc intent + the
            manager-tier dashboard precedent (line 484), plus TENANT_ADMIN since
            this is explicitly a Tenant Admin feature. */}
        <Route path="/users" element={
          <RequireRole allowedRoles={["TENANT_ADMIN", "TENANT_OWNER", "BRANCH_MANAGER", "SHIFT_MANAGER", "HEAD_CHEF", "DIRECTOR", "CHAIRMAN", "CORPORATE_MANAGER"]}>
            <UsersModern />
          </RequireRole>
        } />
        {/* WP-1 D-5: ChangePassword was an unrouted orphan — now wired.
            Release v0 WP-2 fix: guard siết nhầm admin/manager → MỌI user đã login
            (RequireAuth) — SC-013 cần NHÂN VIÊN tự đổi mật khẩu; backend
            /api/v1/auth/change-password chỉ yêu cầu session hợp lệ, không role. */}
        <Route path="/change-password" element={
          <RequireAuth>
            <ChangePassword />
          </RequireAuth>
        } />
        {/* SECURITY FIX (GN-12, PR #432): unguarded duplicate of
            /tenant-admin/organizations (line 546) removed entirely — same
            OrganizationManagement component, but this copy had ZERO guard,
            letting any authenticated session (incl. plain Employee) reach
            tenant-admin org-management UI. Verified 2026-07-10: no live/mounted
            code links to the bare /organizations path — the only references are
            dead code (MainLayout.tsx nav item, utils/navigation.ts nav item),
            neither of which is imported/routed anywhere in App.tsx. Canonical
            route remains /tenant-admin/organizations (TenantAdminLayout guard:
            TENANT_ADMIN/TENANT_OWNER). ISSUE-031 precedent: sibling
            /organizations/new was already removed for the identical reason. */}
        {/* SECURITY FIX (GN-12, PR #432): was mounted with zero guard. File
            header "Stakeholders: Managers, Admins, Chairman" — reporting hub page
            linking to other (separately-guarded) analytics dashboards. Guard
            mirrors the manager-tier dashboard precedent (line 484) plus
            TENANT_ADMIN for the "Admins" stakeholder. */}
        <Route path="/reports" element={
          <RequireRole allowedRoles={["TENANT_ADMIN", "TENANT_OWNER", "BRANCH_MANAGER", "SHIFT_MANAGER", "HEAD_CHEF", "DIRECTOR", "CHAIRMAN", "CORPORATE_MANAGER"]}>
            <Reports />
          </RequireRole>
        } />
        {/* SECURITY FIX (GN-12, PR #432): was mounted with zero guard. Backend
            (app/api/v2/complaints.py) MANAGER_ROLES = get_tenant_manager_roles()
            = TENANT_MANAGER_ROLES_STR (app/constants/roles.py:126-136) — guard
            mirrors backend enforcement exactly. */}
        <Route path="/complaints" element={
          <RequireRole allowedRoles={["TENANT_OWNER", "TENANT_ADMIN", "BRANCH_MANAGER", "SHIFT_MANAGER", "HEAD_CHEF", "CORPORATE_MANAGER"]}>
            <ComplaintsPage />
          </RequireRole>
        } />

        {/* Sprint 47: Landing Page at root (public marketing page) */}
        <Route path="/" element={<Landing />} />

        {/* Fallback: Unknown routes redirect to landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </AppErrorBoundary>
      </PlatformAdminProvider>
    </BrowserRouter>
  );
};

export default App;